/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
import React, {
    useCallback, useEffect, useState, useContext
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { withRouter } from 'react-router-dom';
import { ModalNextProvider, Loader, Link } from '@jutro/components';
import { ServiceManager } from '@jutro/services';
import { error } from '@jutro/logger';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { TranslatorContext, FormattedMessage } from '@jutro/locale';
import {
    withApplicationPropertyContext,
    PaymentComponent
} from 'tdic-components-platform-react';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { messages as commonMessages } from 'gw-platform-translations';
import AuthorizePayment from '../AuthorizePayment/AuthorizePayment';
import styles from './MakePaymentPage.module.scss';
import metadata from './MakePaymentPage.metadata.json5';
import messages from './MakePaymentPage.messages';

const getMinimumDueAmount = (invoice, selectedCurrency) => {
    const invoiceStatus = _.get(invoice, 'invoiceStatus', '');

    return {
        currency: selectedCurrency,
        amount: invoiceStatus !== 'planned'
            ? _.get(invoice, 'amountDue.amount', 0)
            : 0
    };
};

const getPayInFullAmount = (
    invoiceSummary,
    invoice,
    selectedCurrency
) => {
    const paymentGroupId = _.get(invoice, 'paymentGroupId', '');

    const paymentGroupInvoices = _.filter(
        invoiceSummary,
        {
            paymentGroupId
        }
    );

    return {
        currency: selectedCurrency,
        amount: _.sumBy(
            paymentGroupInvoices,
            'amountDue.amount'
        )
    };
};

const createNormaliseData = (
    invoiceSummary,
    selectedCurrency
) => {
    return _.map(invoiceSummary, (invoice) => {
        return {
            dueDate: _.get(invoice, 'dueDate', ''),
            invoiceStatus: _.get(invoice, 'invoiceStatus', ''),
            totalValue: _.get(invoice, 'amountTotal', ''),
            totalPaid: _.get(invoice, 'amountPaid', ''),
            amountDue: _.get(invoice, 'amountDue', ''),
            invoiceNumber: _.get(invoice, 'number', ''),
            invoiceId: _.get(invoice, 'id', ''),
            policyNumber: _.get(
                invoice,
                'invoiceItems[0].policyId',
                ''
            ),
            offeringCode: _.isEmpty(
                _.get(invoice, 'invoiceItems', [])
            )
                ? null
                : _.get(
                    invoice,
                    'invoiceItems[0].offeringCode',
                    ''
                ),
            offeringName: _.isEmpty(
                _.get(invoice, 'invoiceItems', [])
            )
                ? null
                : _.get(
                    invoice,
                    'invoiceItems[0].offeringName',
                    ''
                ),
            minimumAmount: getMinimumDueAmount(
                invoice,
                selectedCurrency
            ),
            payInFullValue: getPayInFullAmount(
                invoiceSummary,
                invoice,
                selectedCurrency
            ),
            baseState: _.get(invoice, 'baseState', ''),
            policyHolderName: _.get(
                invoice,
                'policyHolderName',
                ''
            ),
            billingAddressStreet: _.get(
                invoice,
                'billingAddressStreet',
                ''
            ),
            billingZip: _.get(invoice, 'billingZip', '')
        };
    });
};

const createPaymentInstrumentObject = (
    paymentDetailsVm,
    paymentMethod
) => {
    const paymentDetailsValue = paymentDetailsVm.value;

    const bankAccountData = {
        ...paymentDetailsValue
    };

    const creditCardData = {
        ...paymentDetailsValue
    };

    let detailsValue;

    if (paymentMethod === 'wire') {
        detailsValue = {
            bankAccountData
        };
    } else {
        detailsValue = {
            creditCardData
        };
    }

    return {
        paymentMethod,
        ...detailsValue
    };
};

function MakePaymentPage(props) {
    const {
        authHeader,
        history,
        loggedUserContact
    } = props;

    const localeService = ServiceManager.getService(
        'locale-service'
    );

    const defaultCurrencyCode = localeService
        .getDefaultCurrencyCode()
        .toUpperCase();

    const translator = useContext(TranslatorContext);

    const {
        AccountBillingDetailsService: BillingService
    } = useDependencies(
        'AccountBillingDetailsService'
    );

    const [isLoadingData, updateIsLoadingData] = useState(true);
    const [selectedPage, updateSelectedPage] = useState([]);
    const [
        selectedInvoicesForPayment,
        updateSelectedInvoicesForPayment
    ] = useState([]);
    const [
        selectedPayInFullInvoices,
        updateSelectedPayInFullInvoices
    ] = useState([]);
    const [selectedScreen, updateSelectedScreen] = useState(
        'default'
    );
    const [
        selectedCurrency,
        updateSelectedCurrency
    ] = useState(defaultCurrencyCode);
    const [amountToPay, updateAmountToPay] = useState(null);
    const [
        selectedPaymentType,
        setSelectedPaymentType
    ] = useState('ACH');

    const [creditCardFee, updateCreditCardFee] = useState({
        amount: 0,
        currency: defaultCurrencyCode
    });

    const [totalPayment, updateTotalAmount] = useState({
        amount: 0,
        currency: defaultCurrencyCode
    });

    const [
        invoiceAmountToPay,
        updateInvoiceAmountToPay
    ] = useState(0);

    const [
        invoiceTotalAmountToPay,
        updateInvoiceTotalAmountToPay
    ] = useState(0);

    const [tncAccepted, updateTnCAccepted] = useState(false);

    const [
        billingSummariesFromApi,
        setBillingSummariesFromApi
    ] = useState({});

    const [
        hasCancelledPolicyDueInvoice,
        setHasCancelledPolicyDueInvoice
    ] = useState(false);

    const [
        fetchingCreditCardFee,
        setFetchingCreditCardFee
    ] = useState(false);

    /*
     * This list is populated by getNonPSFStates.
     *
     * Example:
     * ['CA', 'NJ']
     */
    const [
        feeExemptStates,
        setFeeExemptStates
    ] = useState([]);

    const setFetchedData = useCallback((fetchData) => {
        const { amountTotal } = fetchData;

        if (amountTotal) {
            const { currency } = amountTotal;

            updateSelectedCurrency(
                currency.toUpperCase()
            );
        }

        setBillingSummariesFromApi(fetchData);
        updateSelectedPage(fetchData);
    }, []);

    const fetchBillingSummary = useCallback(() => {
        return BillingService
            .getCompletedInvoicesForPayment(authHeader)
            .then((data) => {
                setFetchedData(data);

                BillingService
                    .isHaveCancelledPolicyWithDueInvoice(authHeader)
                    .then(setHasCancelledPolicyDueInvoice)
                    .finally(() => {
                        updateIsLoadingData(false);
                    });

                BillingService
                    .getNonPSFStates(authHeader)
                    .then((states) => {
                        const normalized = Array.isArray(states)
                            ? states
                                .map((state) => _.toUpper(
                                    String(state).trim()
                                ))
                                .filter(Boolean)
                            : [];

                        setFeeExemptStates(normalized);
                    })
                    .catch(() => {
                        setFeeExemptStates([]);
                    });
            })
            .finally(() => {
                updateIsLoadingData(false);
            });
    }, [
        BillingService,
        authHeader,
        setFetchedData
    ]);

    const resetInternalStates = useCallback(() => {
        fetchBillingSummary().finally(() => {
            updateSelectedScreen('default');
            updateSelectedInvoicesForPayment([]);
            updateSelectedPayInFullInvoices([]);
            updateAmountToPay(null);
            updateCreditCardFee({
                amount: 0,
                currency: selectedCurrency
            });
            updateTotalAmount({
                amount: 0,
                currency: selectedCurrency
            });
            setFeeExemptStates([]);
        });
    }, [
        fetchBillingSummary,
        selectedCurrency
    ]);

    const onClickConfirmMakePayment = useCallback(
        (paymentDetailsVm, paymentMethod) => {
            const paymentInstrumentValue = createPaymentInstrumentObject(
                paymentDetailsVm,
                paymentMethod
            );

            updateIsLoadingData(true);

            BillingService.makeDirectBillPayment(
                selectedInvoicesForPayment,
                amountToPay.amount,
                paymentInstrumentValue,
                authHeader
            )
                .then(() => {
                    updateSelectedScreen(
                        'paymentConfirmation'
                    );
                })
                .catch((err) => {
                    error(
                        `MakePaymentPage: onClickConfirmMakePayment - Unable to make a payment ${JSON.stringify(err)}`
                    );

                    ModalNextProvider.showAlert({
                        title: messages.paymentRequestFailed,
                        message: messages
                            .sorryYourPaymentCouldNotBeProcessedAtThisTime,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.ok
                    }).then(() => {
                        resetInternalStates();
                    }, _.noop);
                })
                .finally(() => {
                    updateIsLoadingData(false);
                });
        },
        [
            BillingService,
            selectedInvoicesForPayment,
            amountToPay,
            authHeader,
            resetInternalStates
        ]
    );

    const onClickCancelMakePayment = useCallback(() => {
        history.push('/home');
    }, [history]);

    const onClickCompleteMakePayment = useCallback(() => {
        if (appConfig.persona === 'policyholder') {
            history.push('/home');
        } else {
            resetInternalStates();
        }
    }, [
        history,
        resetInternalStates
    ]);

    const invoiceSummary = createNormaliseData(
        billingSummariesFromApi,
        selectedCurrency
    );

    const uniqueInvoiceSummary = _.filter(
        invoiceSummary,
        (invoice) => invoice.invoiceStatus !== 'planned'
    );

    const MAX_INVOICES_ALLOWED = 5;

    const onUpdateSelectedInvoices = useCallback(
        async (selectedInvoiceSummariesId) => {
            const uniqueSelectedIds = _.uniq(
                selectedInvoiceSummariesId
            );

            setFetchingCreditCardFee(true);

            const selectedInvoiceObjects = [];

            uniqueSelectedIds.forEach((idOrPolicy) => {
                const invoice = _.find(
                    invoiceSummary,
                    {
                        invoiceId: idOrPolicy
                    }
                );

                if (invoice) {
                    selectedInvoiceObjects.push(invoice);
                } else {
                    const policyInvoices = _.filter(
                        invoiceSummary,
                        {
                            policyNumber: idOrPolicy
                        }
                    );

                    policyInvoices.forEach((policyInvoice) => {
                        selectedInvoiceObjects.push(
                            policyInvoice
                        );
                    });
                }
            });

            const invoiceCount = uniqueSelectedIds.length;

            if (invoiceCount > MAX_INVOICES_ALLOWED) {
                setFetchingCreditCardFee(false);

                ModalNextProvider.showAlert({
                    title: messages.invoiceLimitTitle,
                    message: messages.invoiceLimitMessage,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok
                });

                return;
            }

            const selectedInvoiceIds = _.map(
                _.uniqBy(
                    selectedInvoiceObjects,
                    'invoiceId'
                ),
                'invoiceId'
            );

            const hasInvalidInvoiceSequence = _.some(
                selectedInvoiceObjects,
                (selectedInvoice) => {
                    const selectedDueDate = new Date(
                        selectedInvoice.dueDate
                    ).getTime();

                    const earlierUnselectedInvoicesInSamePolicy = _.filter(
                        invoiceSummary,
                        (invoice) => {
                            return (
                                invoice.policyNumber
                                    === selectedInvoice.policyNumber
                                    && invoice.invoiceStatus
                                    !== 'planned'
                                    && invoice.invoiceId
                                    !== selectedInvoice.invoiceId
                                    && new Date(
                                        invoice.dueDate
                                    ).getTime() < selectedDueDate
                                    && !selectedInvoiceIds.includes(
                                        invoice.invoiceId
                                    )
                            );
                        }
                    );

                    return (
                        earlierUnselectedInvoicesInSamePolicy.length
                        > 0
                    );
                }
            );

            if (hasInvalidInvoiceSequence) {
                setFetchingCreditCardFee(false);

                ModalNextProvider.showAlert({
                    title: messages.invoiceOrderTitle,
                    message: messages.invoiceOrderMessage,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok
                });

                return;
            }

            const selectedTotalAmount = _.sumBy(
                selectedInvoiceObjects,
                (invoice) => Number(
                    _.get(
                        invoice,
                        'amountDue.amount',
                        0
                    )
                )
            ).toFixed(2);

            const selectedTotalAmountCompletePay = _.sumBy(
                selectedInvoiceObjects,
                (invoice) => Number(
                    _.get(
                        invoice,
                        'payInFullValue.amount',
                        0
                    )
                )
            ).toFixed(2);

            updateInvoiceAmountToPay(
                selectedTotalAmount
            );

            updateInvoiceTotalAmountToPay(
                selectedTotalAmountCompletePay
            );

            updateAmountToPay({
                amount: selectedTotalAmount,
                currency: selectedCurrency
            });

            updateSelectedInvoicesForPayment(
                uniqueSelectedIds
            );

            updateSelectedPayInFullInvoices(
                selectedPayInFullInvoices
            );

            try {
                /*
                 * Expected response:
                 *
                 * [
                 *     'CA',
                 *     'NJ'
                 * ]
                 */
                const nonPSFStatesResponse = await BillingService.getNonPSFStates(
                    authHeader
                );

                const normalizedNonPSFStates = Array.isArray(
                    nonPSFStatesResponse
                )
                    ? nonPSFStatesResponse
                        .map((state) => _.toUpper(
                            String(state).trim()
                        ))
                        .filter(Boolean)
                    : [];

                setFeeExemptStates(
                    normalizedNonPSFStates
                );

                /*
                 * Determine whether any selected invoice belongs
                 * to a state returned by getNonPSFStates.
                 */
                const hasNonPSFStateInvoice = _.some(
                    selectedInvoiceObjects,
                    (invoice) => normalizedNonPSFStates
                        .includes(
                            _.toUpper(
                                _.get(
                                    invoice,
                                    'baseState',
                                    ''
                                )
                            )
                        )
                );

                /*
                 * Do not call getCreditCardFee when none of the
                 * selected invoices belongs to a returned state.
                 */
                if (!hasNonPSFStateInvoice) {
                    updateTotalAmount({
                        amount: selectedTotalAmount,
                        currency: selectedCurrency
                    });

                    updateCreditCardFee({
                        amount: 0,
                        currency: selectedCurrency
                    });

                    return;
                }

                const totalAmountForCCFee = _.sumBy(
                    selectedInvoiceObjects,
                    (invoice) => {
                        const amount = Number(
                            _.get(
                                invoice,
                                'amountDue.amount',
                                0
                            )
                        );

                        /*
                         * Preserve the existing WA Cyber Liability
                         * exclusion.
                         */
                        if (
                            invoice.offeringCode
                            === 'PLCyberLiab_TDIC'
                            && _.toUpper(
                                invoice.baseState
                            ) === 'WA'
                        ) {
                            return 0;
                        }

                        return amount;
                    }
                ).toFixed(2);

                const creditCardFeeResponse = await BillingService.getCreditCardFee(
                    totalAmountForCCFee,
                    authHeader
                );

                const creditCardFeeAmount = Number(
                    creditCardFeeResponse
                ) || 0;

                const grandTotal = (
                    Number(selectedTotalAmount)
                    + creditCardFeeAmount
                ).toFixed(2);

                updateTotalAmount({
                    amount: grandTotal,
                    currency: selectedCurrency
                });

                updateCreditCardFee({
                    amount: creditCardFeeAmount,
                    currency: selectedCurrency
                });
            } catch (err) {
                console.error(
                    'Non-PSF state or credit-card fee error:',
                    err
                );

                updateTotalAmount({
                    amount: selectedTotalAmount,
                    currency: selectedCurrency
                });

                updateCreditCardFee({
                    amount: 0,
                    currency: selectedCurrency
                });

                setFeeExemptStates([]);
            } finally {
                setFetchingCreditCardFee(false);
            }
        },
        [
            invoiceSummary,
            selectedCurrency,
            selectedPayInFullInvoices,
            BillingService,
            authHeader
        ]
    );

    const getSelectedInvoiceList = useCallback(() => {
        const selectedInvoices = _.flatMap(
            selectedInvoicesForPayment,
            (invoiceIdOrPolicy) => {
                const invoice = _.find(
                    invoiceSummary,
                    {
                        invoiceId: invoiceIdOrPolicy
                    }
                );

                if (invoice) {
                    return [invoice];
                }

                return _.filter(
                    invoiceSummary,
                    {
                        policyNumber: invoiceIdOrPolicy
                    }
                );
            }
        );

        return _.uniqBy(
            selectedInvoices,
            'invoiceId'
        );
    }, [
        invoiceSummary,
        selectedInvoicesForPayment
    ]);

    const selectedInvoiceList = getSelectedInvoiceList();

    const selectedPolicyKeys = _.uniq(
        selectedInvoiceList
            .map((invoice) => _.get(invoice, 'policyNumber', ''))
            .map((policyNumber) => policyNumber.split('(')[0].trim())
            .filter(Boolean)
    );

    const hasFeeExemptInvoice = _.some(
        selectedInvoiceList,
        (invoice) => feeExemptStates.includes(
            _.toUpper(
                _.get(invoice, 'baseState', '')
            )
        )
    );

    const hasOnlyOtherStateInvoices = (
        selectedInvoiceList.length > 0
        && !hasFeeExemptInvoice
    );

    const allInvoicesArePsf = (
        uniqueInvoiceSummary.length > 0
    && feeExemptStates.length > 0
    && !_.some(
        uniqueInvoiceSummary,
        (invoice) => feeExemptStates.includes(
            _.toUpper(
                _.get(invoice, 'baseState', '')
            )
        )
    )
    );

    const isAmountToPayValid = useCallback(
        (amount) => {
            return (
                invoiceAmountToPay < amount
                || amount === 0
                || _.isNull(amount)
            );
        },
        [invoiceAmountToPay]
    );

    const isAmountMatches = useCallback(() => {
        if (!amountToPay) {
            return false;
        }

        const grandTotal = Number(
            parseFloat(amountToPay.amount)
            + creditCardFee.amount
        ).toFixed(2);

        const totalPayAmount = Number(
            parseFloat(totalPayment.amount)
        ).toFixed(2);

        return totalPayAmount === grandTotal;
    }, [
        totalPayment,
        amountToPay,
        creditCardFee
    ]);

    const getBillingAddress = useCallback(() => {
        return _.get(
            loggedUserContact,
            'accountContact',
            {}
        );
    }, [loggedUserContact]);

    useEffect(() => {
        console.log(loggedUserContact);
        updateIsLoadingData(true);
        fetchBillingSummary();
    }, [fetchBillingSummary, loggedUserContact]);

    const onRadioButtonValueChange = useCallback(
        (value) => {
            setSelectedPaymentType(value);
            updateTnCAccepted(false);
        },
        []
    );

    const getAutopayMsg = useCallback(() => {
        return (
            <FormattedMessage
                {...messages.tncBillingAchAutopay}
                values={{
                    link: (
                        <Link to="/billing-summary">
                            {translator(messages.autopay)}
                        </Link>
                    )
                }}
            />
        );
    }, [translator]);

    if (isLoadingData) {
        return <Loader loaded={!isLoadingData} />;
    }

    const overrideProps = {
        '@field': {
            labelPosition: 'left'
        },

        makePaymentContainerId: {
            visible: (
                selectedScreen === 'default'
                && uniqueInvoiceSummary.length > 0
            ),
            className: fetchingCreditCardFee === true
                ? styles.disable
                : ''
        },

        makePaymentInvoicesTableComponentId: {
            invoiceSummary: uniqueInvoiceSummary,
            selectedInvoices: selectedInvoicesForPayment,
            onUpdateSelectedInvoices,
            selectedPage
        },

        makePaymentSourceComponentId: {
            title: messages.setPaymentSource,
            xCenter: 'bc',
            isDisabled: (
                selectedInvoicesForPayment.length === 0
            ),
            isSelectedInvoiceAmount: (
                amountToPay
                && isAmountToPayValid(amountToPay.amount)
            )
        },

        makePaymentAmountToPayId: {
            visible: (
                !_.isEmpty(uniqueInvoiceSummary)
                && selectedPaymentType === 'CC'
            ),
            defaultCurrency: selectedCurrency,
            onValueChange: updateAmountToPay
        },

        makePaymentConfirmationContainerId: {
            visible: selectedScreen === 'paymentConfirmation'
        },

        makePaymentNoPayableInvoicesContainerId: {
            visible: _.isEmpty(uniqueInvoiceSummary)
        },

        authorizePayment: {
            amountToPay: selectedPaymentType === 'ACH'
                ? amountToPay
                : totalPayment,
            totalAmountToPay: invoiceTotalAmountToPay,
            invoiceList: getSelectedInvoiceList(),
            isValid: (
                tncAccepted
                && !_.isEmpty(selectedInvoicesForPayment)
                && isAmountMatches()
            ),
            billingAddress: getBillingAddress(),
            creditCardFee,
            authHeader,
            history,
            selectPaymentMethod: selectedPaymentType,
            feeExemptStates,
            selectedPolicyKeys
        },

        creditCardFee: {
            value: creditCardFee,
            visible: (
                !_.isEmpty(uniqueInvoiceSummary)
                && selectedPaymentType === 'CC'
                && hasFeeExemptInvoice
            )
        },

        paymentTNC: {
            label: selectedPaymentType === 'ACH'
                ? messages.tncBillingAch
                : (hasOnlyOtherStateInvoices || allInvoicesArePsf)
                    ? messages.tncBillingPsf
                    : messages.tncBilling,
            value: tncAccepted,
            onValueChange: updateTnCAccepted
        },
        bankAccountContent: {
            visible: selectedPaymentType === 'ACH',
            content: getAutopayMsg()
        },

        grandTotal: {
            value: selectedPaymentType === 'ACH'
                ? amountToPay
                : totalPayment,
            visible: (
                selectedPaymentType === 'ACH'
                || (
                    selectedPaymentType === 'CC'
                    && hasFeeExemptInvoice
                )
            )
        },

        makePaymentNoPayableInvoicesMessageId: {
            content: hasCancelledPolicyDueInvoice === true
                ? translator(
                    messages.hasDueinvoicesOnCancelledPolicy
                )
                : translator(
                    messages.noPaymentsAreCurrentlyDue
                )
        },

        selectPaymentMethod: {
            onValueChange: onRadioButtonValueChange,
            value: selectedPaymentType
        },

        creditCardText: {
            visible: (
                amountToPay !== null
                && selectedPaymentType === 'CC'
                && hasOnlyOtherStateInvoices
            )
        }
    };

    const dataForComponent = {
        amountToPay
    };

    const resolvers = {
        resolveClassNameMap: styles,

        resolveComponentMap: {
            paymentpagecomponent: PaymentComponent,
            authorizepaymentcomponent: AuthorizePayment
        },

        resolveCallbackMap: {
            onClickConfirmMakePayment,
            onClickCancelMakePayment,
            onClickCompleteMakePayment
        }
    };

    return (
        <div>
            {fetchingCreditCardFee ? (
                <Loader
                    loaded={!fetchingCreditCardFee}
                    className={styles.overlay}
                />
            ) : null}

            <ViewModelForm
                model={dataForComponent}
                uiProps={metadata.pageContent}
                overrideProps={overrideProps}
                classNameMap={resolvers.resolveClassNameMap}
                componentMap={resolvers.resolveComponentMap}
                callbackMap={resolvers.resolveCallbackMap}
            />
        </div>
    );
}

MakePaymentPage.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired,

    authHeader: PropTypes.shape({}).isRequired,

    loggedUserContact: PropTypes.shape({}).isRequired
};

export default withAuthenticationContext(
    withApplicationPropertyContext(
        withRouter(MakePaymentPage)
    )
);
